/**
 * Feb 2026 · Raw Material (RM) Master.
 *
 * Catalogue of every incoming good — warp/weft yarn, rubber, spandex,
 * beam, dyes, chemicals. Sits next to the FG Master. CRUD only; stock
 * movement stays under Inventory (this page shows `current_stock`
 * read-only as a cue).
 *
 * Feb 2026 · Structured RM Code composer — 8 attributes join with `-` to
 * form the canonical RM code, but the code field itself stays editable
 * for legacy stock:
 *   Category · Denier/Count · TPM/ROTO · Type · Colour · Supplier
 *   · Devp No · Shade No
 *
 * Backend: `RawMaterial` model in models.py, `/api/raw-materials` CRUD.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { useAuth } from "@/lib/auth";
import { exportToCSV } from "@/lib/csv";
import SearchablePicker from "@/components/SearchablePicker";
import {
  PlusIcon, PencilSimpleIcon, TrashIcon, DownloadSimpleIcon,
  MagnifyingGlassIcon, PackageIcon, ArrowsClockwiseIcon, WarningIcon,
  SwatchesIcon,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["Warp Yarn", "Weft Yarn", "Rubber", "Spandex", "Beam", "Dye", "Chemical", "Other"];
const UOMS = ["kg", "g", "m", "cone", "roll", "L", "pcs"];
const ITEM_TYPES = ["Raw Material", "Semi Finished", "Finished Goods", "Consumable", "Spare Parts", "Packing Material", "Scrap"];
// Fallback list used only before the /rm-category-options API responds. Once
// loaded, `categoryOpts` state overrides this so admin additions show up.
const CATEGORIES_FALLBACK = ["Warp Yarn", "Weft Yarn", "Rubber", "Spandex", "Beam", "Dye", "Chemical", "Other"];

// Static 3-letter mnemonics used ONLY as a fallback when a category came
// from the seed list. Dynamic categories authored by admins carry their
// own `code` from /rm-category-options and take precedence in composeRmCode.
const CAT_ABBR_FALLBACK = {
  "Warp Yarn": "WRP",
  "Weft Yarn": "WFT",
  "Rubber":    "RUB",
  "Spandex":   "SPX",
  "Beam":      "BEA",
  "Dye":       "DYE",
  "Chemical":  "CHM",
  "Other":     "OTH",
};

const empty = {
  code: "", name: "", category: "Warp Yarn", uom: "kg",
  supplier_id: "", current_stock: 0, unit_cost: 0, description: "",
  denier_count: "", tpm_roto: "", rm_type: "", colour: "",
  devp_no: "", shade_no: "",
  // Mar 1, 2026 · Batch tracking is on by default so every fresh RM
  // participates in the Batch & Traceability engine unless a user
  // explicitly opts out.
  batch_tracking: true,
};

const inp = "h-9 w-full bg-[var(--bg-0)] border border-[var(--border)] px-3 rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const inpNum = inp + " text-right font-mono tabular-nums";

function F({ label, children, hint }) {
  return (
    <label className="block">
      <div className="overline mb-1 text-[10px]">{label}{hint && <span className="ml-1 text-zinc-600 lowercase tracking-normal">· {hint}</span>}</div>
      {children}
    </label>
  );
}

/**
 * Compose the canonical RM code from structured attributes. `catCodeMap`
 * comes from the /rm-category-options API and maps category name → code
 * so admin-authored categories work; the seed mnemonics remain the
 * fallback for categories missing a code. Empty segments are dropped.
 */
function composeRmCode(f, supplierShort, catCodeMap) {
  const catCode = (catCodeMap && catCodeMap[f.category]) || CAT_ABBR_FALLBACK[f.category] || (f.category || "").slice(0, 3);
  // Feb 2026 · Devp No + Shade No are emitted verbatim (no D/S prefix) so
  // codes like "NSAM-1234/3" render exactly as typed. Slashes and dashes
  // are preserved by the whitespace-only cleanup below.
  const seg = [
    catCode,
    f.denier_count,
    f.tpm_roto,
    f.rm_type,
    f.colour,
    supplierShort,
    f.devp_no,
    f.shade_no,
  ]
    .map(x => String(x || "").trim().replace(/\s+/g, "").toUpperCase())
    .filter(Boolean);
  return seg.join("-");
}

export default function RawMaterialMaster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Administrator";
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Feb 2026 · Dynamic RM categories sourced from /rm-category-options.
  // Falls back to the seed list until the API responds so first paint
  // still shows a usable picker.
  const [categoryOpts, setCategoryOpts] = useState([]);
  // Feb 2026 · RM code composer sub-master pickers.
  const [denierOpts, setDenierOpts] = useState([]);
  const [tpmOpts, setTpmOpts] = useState([]);
  const [yarnTypeOpts, setYarnTypeOpts] = useState([]);
  // Phase 1 · Foundation-master pickers for the Classification + Stock Policy sections.
  const [itemCategories, setItemCategories] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [uomOpts, setUomOpts] = useState([]);
  const [hsnOpts, setHsnOpts] = useState([]);
  const [taxOpts, setTaxOpts] = useState([]);
  const [reorderPolicies, setReorderPolicies] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Feb 2026 · Delete-usage warning dialog. When an operator triggers
  // delete on an RM that's referenced elsewhere, we surface the usage
  // payload (BOMs, POs, PRs, lots) here so they can act before deleting.
  const [deleteWarning, setDeleteWarning] = useState(null); // { rm, usage }
  const [codeManual, setCodeManual] = useState(false);   // user typed in code field directly
  // Feb 2026 · Smart Lock — usage snapshot for the currently-edited RM.
  // `linked: true` means the RM is referenced by a BOM line / inventory
  // lot; the UI then locks the `code` field and disables the delete
  // button. `is_active=false` retires the RM from new BOM dropdowns.
  const [usage, setUsage] = useState({ linked: false, reasons: [] });

  const load = async () => {
    setLoading(true);
    try {
      // Feb 2026 · Categories sourced from the unified Item Category
      // master (consolidated with the legacy rm_category_options).
      const [{ data: rm }, { data: sp }, { data: cats }, { data: dens }, { data: tpms }, { data: yts },
             { data: ic }, { data: ig }, { data: br }, { data: uo }, { data: hs }, { data: tx }, { data: rp }, { data: gd }] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
        api.get("/item-categories/").catch(() => ({ data: [] })),
        api.get("/denier-count-options/").catch(() => ({ data: [] })),
        api.get("/tpm-roto-options/").catch(() => ({ data: [] })),
        // Feb 2026 · Yarn Type consolidated into Item Group. The RM code
        // composer picker now reads item_groups (all active), filtered
        // client-side to those parented under the Yarn Item Category
        // when one exists — falls back to every group otherwise.
        api.get("/item-groups/").catch(() => ({ data: [] })),
        api.get("/item-categories/").catch(() => ({ data: [] })),
        api.get("/item-groups/").catch(() => ({ data: [] })),
        api.get("/brands/").catch(() => ({ data: [] })),
        api.get("/uom-masters/").catch(() => ({ data: [] })),
        api.get("/hsn-codes/").catch(() => ({ data: [] })),
        api.get("/tax-masters/").catch(() => ({ data: [] })),
        api.get("/reorder-policies/").catch(() => ({ data: [] })),
        api.get("/godowns/").catch(() => ({ data: [] })),
      ]);
      setRows(rm);
      setSuppliers(sp || []);
      // Filter out inactive so retired categories drop from new-RM pickers
      // while preserving history on old RMs (they keep their category name).
      setCategoryOpts((cats || []).filter(c => c.is_active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setDenierOpts(dens || []);
      setTpmOpts(tpms || []);
      // Feb 2026 · Yarn Type merged into Item Group. Show every active
      // Item Group so buyers can pick a fibre family regardless of the
      // Category assignment (mirrors the Classification-section picker
      // behaviour — "all groups always available").
      setYarnTypeOpts((yts || []).filter(g => g.is_active !== false));
      setItemCategories((ic || []).filter(x => x.is_active !== false));
      setItemGroups((ig || []).filter(x => x.is_active !== false));
      setBrands((br || []).filter(x => x.is_active !== false));
      setUomOpts((uo || []).filter(x => x.is_active !== false));
      setHsnOpts((hs || []).filter(x => x.is_active !== false));
      setTaxOpts((tx || []).filter(x => x.is_active !== false));
      setReorderPolicies((rp || []).filter(x => x.is_active !== false));
      setGodowns((gd || []).filter(x => x.is_active !== false));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load raw materials");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const supplierName = (id) => suppliers.find(s => s.id === id)?.name || "";
  // Feb 2026 · Prefer explicit supplier.short_code; fall back to name[:3]
  // so legacy suppliers without a short code still yield a segment.
  const supplierShort = (id) => {
    const s = suppliers.find(x => x.id === id);
    if (!s) return "";
    return (s.short_code && s.short_code.trim()) || (s.name || "").slice(0, 3);
  };

  // Category name → code map, sourced from dynamic /rm-category-options.
  // Feeds composeRmCode and the picker below.
  const catCodeMap = useMemo(
    () => Object.fromEntries((categoryOpts || []).filter(c => c.name).map(c => [c.name, (c.code || "").trim()])),
    [categoryOpts]
  );
  const categoryNames = useMemo(
    () => (categoryOpts.length ? categoryOpts.map(c => c.name).filter(Boolean) : CATEGORIES_FALLBACK),
    [categoryOpts]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (!needle) return true;
      return [r.code, r.name, r.category, r.description, supplierName(r.supplier_id), r.denier_count, r.tpm_roto, r.rm_type, r.colour, r.devp_no, r.shade_no]
        .join(" ").toLowerCase().includes(needle);
    });
  }, [rows, q, categoryFilter, suppliers]);

  const openNew = () => { setForm(empty); setEditing(null); setCodeManual(false); setUsage({ linked: false, reasons: [] }); setOpen(true); };
  const openEdit = async (r) => {
    setForm({ ...empty, ...r });
    setEditing(r);
    setCodeManual(true);
    setUsage({ linked: false, reasons: [] });
    setOpen(true);
    try {
      const { data } = await api.get(`/master/usage/rm/${r.id}`);
      setUsage(data);
    } catch { /* usage best-effort — falls back to unlocked */ }
  };

  // ── Auto-compose the RM code whenever any structured attribute changes
  // (unless the user has explicitly edited the code field). Keeps the
  // preview live so users see what they'll get before saving.
  const composedCode = useMemo(
    () => composeRmCode(form, supplierShort(form.supplier_id), catCodeMap),
    [form.category, form.denier_count, form.tpm_roto, form.rm_type, form.colour, form.supplier_id, form.devp_no, form.shade_no, suppliers, catCodeMap]
  );
  useEffect(() => {
    if (!codeManual) setForm(f => ({ ...f, code: composedCode }));
  }, [composedCode, codeManual]);

  // Feb 2026 · Duplicate-code live check. Fires 350ms after the user stops
  // typing (both the composed and hand-typed paths converge on `form.code`).
  // Server-side POST/PUT re-check the same rule as an authoritative guard.
  const [codeAvailable, setCodeAvailable] = useState(null);
  const [codeConflict, setCodeConflict] = useState(null);

  // Feb 07, 2026 · RM Code Patch — Development → Commercial promotion.
  // Admin-only shade-apply flow. Reject when a shade is already stamped
  // (b4 policy) so the transition is one-way per shade — force explicit
  // "Clear Shade" first if a re-stamp is needed.
  const [shadeTarget, setShadeTarget] = useState(null);
  const [shadeInput, setShadeInput] = useState("");
  const [shadeReason, setShadeReason] = useState("");
  const [shadePreview, setShadePreview] = useState(null);
  const [shadeBusy, setShadeBusy] = useState(false);
  const openShade = async (r) => {
    setShadeTarget(r);
    setShadeInput("");
    setShadeReason("");
    setShadePreview(null);
    try {
      const { data } = await api.get(`/raw-materials/${r.id}/shade-impact`);
      setShadePreview(data);
    } catch (e) { /* ignore — preview refetches on shade input */ }
  };
  const previewShade = async () => {
    if (!shadeTarget) return;
    try {
      const { data } = await api.get(`/raw-materials/${shadeTarget.id}/shade-impact`, { params: { shade_no: shadeInput } });
      setShadePreview(data);
    } catch (e) { /* ignore */ }
  };
  const applyShade = async () => {
    if (!shadeTarget) return;
    if (!shadeInput.trim()) { toast.error("Enter a shade number"); return; }
    if (!shadeReason.trim()) { toast.error("Enter a reason for the audit log"); return; }
    setShadeBusy(true);
    try {
      const { data } = await api.post(`/raw-materials/${shadeTarget.id}/apply-shade`, {
        shade_no: shadeInput.trim(),
        reason: shadeReason.trim(),
      });
      const rowsTouched = Object.values(data.cascaded_counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Promoted ${data.old_code} → ${data.new_code} · ${rowsTouched} downstream row(s) updated`);
      setShadeTarget(null);
      // Refresh grid so the new code renders immediately.
      const { data: fresh } = await api.get("/raw-materials/");
      setRows(fresh || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to promote RM");
    } finally {
      setShadeBusy(false);
    }
  };
  useEffect(() => {
    const code = (form.code || "").trim();
    if (!code) { setCodeAvailable(null); setCodeConflict(null); return; }
    const timer = setTimeout(async () => {
      try {
        const params = { code };
        if (editing?.id) params.exclude_id = editing.id;
        const { data } = await api.get("/raw-materials/check-code", { params });
        setCodeAvailable(!!data.available);
        setCodeConflict(data.conflict || null);
      } catch { setCodeAvailable(null); setCodeConflict(null); }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.code, editing]);

  const save = async () => {
    if (!form.code.trim()) return toast.error("RM Code is required");
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        min_stock: Number(form.safety_stock) || 0,
        // Feb 27, 2026 · current_stock is NEVER sent from the RM Master
        // form — the backend guard rejects any change to it. When editing
        // we preserve the existing DB value; for new codes it defaults
        // to zero and gets seeded via Opening Stock Entry.
        current_stock: editing ? Number(editing.current_stock) || 0 : 0,
        unit_cost: Number(form.unit_cost) || 0,
      };
      if (editing) {
        await api.put(`/raw-materials/${editing.id}`, payload);
        toast.success(`Updated ${payload.code}`);
      } else {
        await api.post("/raw-materials/", payload);
        toast.success(`Added ${payload.code}`);
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    // Feb 2026 · Smart Lock · block delete when the RM is used elsewhere
    // AND surface a "what will break" preview so the operator can update
    // affected BOMs before removing the master row.
    try {
      const { data } = await api.get(`/master/usage/rm/${r.id}`);
      if (data.linked) {
        setDeleteWarning({ rm: r, usage: data });
        return;
      }
    } catch { /* usage best-effort */ }
    if (!window.confirm(`Delete ${r.code} · ${r.name}?`)) return;
    try {
      await api.delete(`/raw-materials/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const badgeCls = (cat) => ({
    "Warp Yarn":  "bg-blue-500/15 text-blue-300 border-blue-500/30",
    "Weft Yarn":  "bg-sky-500/15 text-sky-300 border-sky-500/30",
    "Rubber":     "bg-orange-500/15 text-orange-300 border-orange-500/30",
    "Spandex":    "bg-pink-500/15 text-pink-300 border-pink-500/30",
    "Beam":       "bg-purple-500/15 text-purple-300 border-purple-500/30",
    "Dye":        "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Chemical":   "bg-amber-500/15 text-amber-300 border-amber-500/30",
    "Other":      "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  }[cat] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30");

  return (
    <div className="p-6 space-y-5" data-testid="raw-material-master-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Incoming Goods</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Raw Material (RM) Master</h1>
          <p className="text-xs text-zinc-500 mt-1">Yarn · Rubber · Spandex · Beam · Dyes · Chemicals — everything that goes INTO production</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="new-rm-btn" onClick={openNew} className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New Raw Material
          </button>
          <button
            data-testid="rm-export-csv"
            onClick={() => exportToCSV("raw_materials.csv", rows, [
              { key: "code", label: "RM Code" },
              { key: "name", label: "Name" },
              { key: "category", label: "Category" },
              { key: "denier_count", label: "Denier/Count" },
              { key: "tpm_roto", label: "TPM/ROTO" },
              { key: "rm_type", label: "Type" },
              { key: "colour", label: "Colour" },
              { key: "devp_no", label: "Devp No" },
              { key: "shade_no", label: "Shade No" },
              { key: "uom", label: "UOM" },
              { key: "min_stock", label: "Min Stock" },
              { key: "current_stock", label: "Current Stock" },
              { key: "unit_cost", label: "Unit Cost" },
              { key: "description", label: "Description" },
            ])}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"
          >
            <DownloadSimpleIcon size={14} weight="bold" />Export CSV
          </button>
        </div>
      </div>

      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[280px] max-w-md">
            <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="rm-search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search RM code, name, supplier, denier, type, colour…"
              className="w-full h-10 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="overline text-[10px] mr-1">Category</span>
            <button
              data-testid="rm-cat-all"
              onClick={() => setCategoryFilter("")}
              className={`h-7 px-3 text-xs rounded-full border ${!categoryFilter ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}
            >All</button>
            {categoryNames.map(c => (
              <button
                key={c}
                data-testid={`rm-cat-${c.replace(/\s+/g,'-').toLowerCase()}`}
                onClick={() => setCategoryFilter(c)}
                className={`h-7 px-3 text-xs rounded-full border ${categoryFilter === c ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}
              >{c}</button>
            ))}
          </div>
          <span className="ml-auto text-xs text-zinc-500" data-testid="rm-count">{filtered.length} of {rows.length}</span>
        </div>

        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">RM Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Denier/Count</th>
                <th className="px-3 py-2 text-left">Type · Colour</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-right">Min / Cur</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="text-center py-12 text-zinc-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-zinc-500">
                  {rows.length === 0 ? "No raw materials yet — click New Raw Material to add your first one." : `No results for "${q || categoryFilter}"`}
                </td></tr>
              )}
              {!loading && filtered.map(r => {
                const low = Number(r.current_stock || 0) < Number(r.min_stock || 0);
                return (
                  <tr key={r.id} data-testid={`rm-row-${r.code}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-3 py-2 font-mono text-[var(--accent)] whitespace-nowrap">{r.code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(r.category)}`}>{r.category}</span>
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">
                      {r.denier_count || <span className="text-zinc-600">—</span>}
                      {r.tpm_roto && <div className="text-[10px] text-zinc-500">{r.tpm_roto}</div>}
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">
                      {r.rm_type || <span className="text-zinc-600">—</span>}
                      {r.colour && <div className="text-[10px] text-zinc-500">{r.colour}</div>}
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">{supplierName(r.supplier_id) || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      <span className="text-zinc-500">{Number(r.min_stock || 0).toLocaleString("en-IN")}</span>
                      <span className="text-zinc-600 mx-1">/</span>
                      <span className={low ? "text-red-400 font-semibold" : "text-emerald-300"} title={low ? "Below minimum stock" : "OK"}>{Number(r.current_stock || 0).toLocaleString("en-IN")}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-zinc-400">{r.uom}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">₹{Number(r.unit_cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {r.shade_no && (
                          <span data-testid={`rm-shade-badge-${r.code}`} title={`Commercial ID promoted from ${r.development_id || "—"} on ${r.commercial_id_promoted_at || "—"}`} className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-purple-500/15 text-purple-300 border border-purple-500/30 mr-1">
                            SHADE·{r.shade_no}
                          </span>
                        )}
                        <RowActions
                          testid={`rm-actions-${r.code}`}
                          items={[
                            { label: "Apply Shade → Promote to Commercial", testid: `rm-shade-${r.code}`, show: isAdmin && !r.shade_no, onClick: () => openShade(r) },
                            { label: "Edit", testid: `rm-edit-${r.code}`, onClick: () => openEdit(r) },
                            { label: "Delete", tone: "danger", testid: `rm-del-${r.code}`, onClick: () => remove(r) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rm-dialog" description="Add or edit a raw material — sets code, category, denier, TPM, yarn type, opening stock and unit cost.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PackageIcon size={18} weight="bold" className="text-[var(--accent)]" />
              {editing ? `Edit ${editing.code}` : "New Raw Material"}
            </DialogTitle>
          </DialogHeader>

          {/* RM Code preview / editor · auto-composed from the 8 attributes */}
          <div className={`rounded-sm border p-3 mb-2 ${usage.linked ? "border-amber-500/50 bg-amber-500/5" : "border-[var(--accent)]/40 bg-[var(--accent)]/5"}`}>
            <div className="flex items-center justify-between mb-1">
              <div className={`overline text-[10px] ${usage.linked ? "text-amber-300" : "text-[var(--accent)]"}`}>RM Code {usage.linked ? "· LOCKED (in use)" : (codeManual ? "(edited manually)" : "(auto)")}</div>
              {/* Feb 2026 · Live duplicate-code chip · flips to red when the
                  code collides with another active/inactive RM (case-insensitive) */}
              {codeAvailable === false && codeConflict && (
                <span
                  data-testid="rm-code-conflict"
                  title={`Collides with '${codeConflict.code}' · ${codeConflict.name}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest bg-red-500/15 text-red-300 border-red-500/40"
                >
                  Duplicate · {codeConflict.name || codeConflict.code}
                </span>
              )}
              {codeAvailable === true && form.code.trim() && (
                <span
                  data-testid="rm-code-available"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                >
                  Available
                </span>
              )}
              {!usage.linked && codeManual && (
                <button
                  data-testid="rm-code-auto"
                  onClick={() => { setCodeManual(false); setForm(f => ({ ...f, code: composedCode })); }}
                  className="text-[10px] flex items-center gap-1 text-[var(--accent)] hover:opacity-80"
                  title="Re-generate from structured fields"
                >
                  <ArrowsClockwiseIcon size={11} weight="bold" />Reset to auto
                </button>
              )}
            </div>
            <input
              data-testid="rm-code"
              value={form.code}
              disabled={usage.linked}
              onChange={e => { setCodeManual(true); setForm({ ...form, code: e.target.value.toUpperCase() }); }}
              className={inp + " font-mono text-[var(--accent)] text-base disabled:opacity-70 disabled:cursor-not-allowed"}
              placeholder="Fill fields below · code auto-composes"
            />
            {usage.linked && (
              <div className="mt-1.5 text-[10px] text-amber-300 flex items-center gap-1">
                Locked because this RM is referenced by: {usage.reasons.join(", ")}. Descriptive fields below stay editable.
              </div>
            )}
          </div>

          {/* Feb 2026 · Active toggle · retires the RM from NEW BOM dropdowns
              while preserving all historical BOM lines & inventory lots. */}
          <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-0)] p-2 mb-3 flex items-center justify-between">
            <div>
              <div className="overline text-[10px]">Lifecycle</div>
              <div className="text-xs text-zinc-400">Inactive items are hidden from new BOM / inventory pickers · historical data preserved</div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                data-testid="rm-is-active"
                type="checkbox"
                checked={form.is_active !== false}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
              <span className={form.is_active !== false ? "text-emerald-300 font-mono uppercase tracking-widest" : "text-zinc-500 font-mono uppercase tracking-widest"}>
                {form.is_active !== false ? "Active" : "Inactive"}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Name *">
              <input data-testid="rm-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inp} placeholder="e.g. Cotton 40s Combed Black" />
            </F>
            <F label="Category *" hint="Feeds RM code · sourced from Item Category master">
              <Select value={form.category} onValueChange={v => {
                // Feb 2026 · Two-way sync — picking here also sets item_category_id
                // silently so future stock/reporting features have the FK.
                const cat = categoryOpts.find(c => c.name === v);
                setForm({ ...form, category: v, item_category_id: cat?.id || form.item_category_id });
              }}>
                <SelectTrigger data-testid="rm-category" className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {categoryOpts.length > 0
                    ? categoryOpts.map(c => (
                        <SelectItem key={c.id} value={c.name}>
                          <span className="font-mono text-[var(--accent)]">{c.code}</span> · {c.name}
                        </SelectItem>
                      ))
                    : categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Denier / Count" hint="e.g. 40S, 150D, 20/2">
              <Select value={form.denier_count || "__none__"} onValueChange={v => setForm({ ...form, denier_count: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="rm-denier" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {denierOpts.map(o => <SelectItem key={o.id} value={o.code || o.name}><span className="font-mono text-[var(--accent)]">{o.code}</span> · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="TPM / ROTO" hint="Twists per metre or ROTO">
              <Select value={form.tpm_roto || "__none__"} onValueChange={v => setForm({ ...form, tpm_roto: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="rm-tpm" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {tpmOpts.map(o => <SelectItem key={o.id} value={o.code || o.name}><span className="font-mono text-[var(--accent)]">{o.code}</span> · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Item Group" hint="Feeds RM code · parent category chip on the right">
              <Select value={form.rm_type || "__none__"} onValueChange={v => {
                if (v === "__none__") { setForm({ ...form, rm_type: "", item_group_id: "" }); return; }
                const grp = yarnTypeOpts.find(g => (g.code || g.name) === v);
                setForm({ ...form, rm_type: v, item_group_id: grp?.id || form.item_group_id });
              }}>
                <SelectTrigger data-testid="rm-type" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {/* Feb 2026 · Parent-category chip on the right of every
                      Item Group option so buyers see the hierarchy without
                      leaving the RM dialog. Chip uses the parent's short
                      code + a subtle border to keep the row readable. */}
                  {(() => {
                    const catMap = Object.fromEntries((itemCategories || []).map(c => [c.id, c]));
                    return yarnTypeOpts.map(o => {
                      const parent = catMap[o.category_id];
                      return (
                        <SelectItem key={o.id} value={o.code || o.name}>
                          <span className="inline-flex items-center gap-2">
                            <span className="font-mono text-[var(--accent)]">{o.code}</span>
                            <span>· {o.name}</span>
                            {parent && (
                              <span
                                className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-zinc-400 bg-[var(--bg-0)]"
                                title={parent.name}
                              >{parent.code}</span>
                            )}
                          </span>
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
            </F>
            <F label="Colour">
              <input data-testid="rm-colour" value={form.colour} onChange={e => setForm({ ...form, colour: e.target.value })} className={inp} placeholder="Black, White, Raw White…" />
            </F>
            <F label="Supplier">
              <SearchablePicker
                testid="rm-supplier"
                value={form.supplier_id || ""}
                onChange={(v) => setForm({ ...form, supplier_id: v })}
                placeholder="Select supplier"
                headerHint="Search by supplier code or name"
                options={suppliers.map(s => ({ value: s.id, code: s.code || "", name: s.name }))}
                clearable
              />
            </F>
            <F label="Devp No" hint="Development number · e.g. NSAM-1234/3">
              <input data-testid="rm-devp" value={form.devp_no} onChange={e => setForm({ ...form, devp_no: e.target.value })} className={inp} placeholder="e.g. NSAM-1234/3" />
            </F>
            <F label="Shade No">
              <input data-testid="rm-shade" value={form.shade_no} onChange={e => setForm({ ...form, shade_no: e.target.value })} className={inp} placeholder="e.g. 4521 or 4521/A" />
            </F>

            <F label="UOM">
              <Select value={form.uom} onValueChange={v => setForm({ ...form, uom: v })}>
                <SelectTrigger data-testid="rm-uom" className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {UOMS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Unit Cost (₹)">
              <input data-testid="rm-unit-cost" type="number" step="0.01" min="0" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} className={inpNum} />
            </F>
            {/* Mar 1, 2026 · Top-row "Min Stock" field removed — replaced
                by Safety Stock (+ Max Stock + Reorder Qty) inside the
                Stock Policy section below. Keeping only one authoritative
                min-stock knob avoids ambiguity for planners. */}
            {/* Feb 27, 2026 · Opening / Current Stock input removed. All
                opening balances now flow through the dedicated
                "Opening Stock Entry" menu (Purchase & Inventory) so
                every seed writes an audited inventory_txns row with
                txn_type='Opening'. The read-only balance column on the
                list view continues to reflect the live current_stock. */}
            <div className="md:col-span-3">
              <F label="Description">
                <textarea data-testid="rm-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inp + " py-2 h-auto"} placeholder="Any additional notes about this raw material" />
              </F>
            </div>
          </div>

          {/* ── Phase 1 · Classification (Feb 2026) ─────────────────────
              Item Category & Item Group live in the top section (they
              double as the RM code composer inputs). Classification
              only carries the truly-new Item Master attributes so we
              don't duplicate any picker. */}
          <div className="mt-4 rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/50 p-3">
            <div className="overline text-[10px] text-[var(--accent)] mb-2">Classification</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <F label="Item Type">
                <Select value={form.item_type || "Raw Material"} onValueChange={v => setForm({ ...form, item_type: v })}>
                  <SelectTrigger data-testid="rm-item-type" className={inp}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Brand">
                <Select value={form.brand_id || "__none__"} onValueChange={v => setForm({ ...form, brand_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rm-brand" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="HSN / SAC Code">
                <Select value={form.hsn_code || "__none__"} onValueChange={v => {
                  if (v === "__none__") { setForm({ ...form, hsn_code: "" }); return; }
                  // Auto-fill tax_code from HSN's default_tax_rate when possible.
                  const h = hsnOpts.find(x => x.code === v);
                  const tax = h && h.default_tax_rate
                    ? (taxOpts.find(t => t.tax_type === "GST" && Math.abs(t.rate - h.default_tax_rate) < 0.01)?.code || form.tax_code)
                    : form.tax_code;
                  setForm({ ...form, hsn_code: v, tax_code: tax });
                }}>
                  <SelectTrigger data-testid="rm-hsn" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {hsnOpts.map(h => <SelectItem key={h.id} value={h.code}><span className="font-mono text-[var(--accent)]">{h.code}</span> · {h.name} <span className="text-zinc-500 text-xs">({h.default_tax_rate}%)</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Tax Code">
                <Select value={form.tax_code || "__none__"} onValueChange={v => setForm({ ...form, tax_code: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rm-tax" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {taxOpts.map(t => <SelectItem key={t.id} value={t.code}><span className="font-mono text-[var(--accent)]">{t.code}</span> · {t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            </div>
          </div>

          {/* ── Phase 1 · Stock Policy (Feb 2026) ───────────────────────
              Purchase UOM + Conversion drive PO/GRN unit conversion.
              Batch/Lot/Expiry flags gate the Batch & Traceability engine
              (Phase 2). Reorder + Max + Safety together power the MRP
              shortage report in Phase 3. Default Warehouse pre-selects
              the GRN destination for this item. */}
          <div className="mt-3 rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/50 p-3">
            <div className="overline text-[10px] text-[var(--accent)] mb-2">Stock Policy</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <F label="Purchase UOM" hint="If different from stock UOM">
                <Select value={form.purchase_uom || "__none__"} onValueChange={v => setForm({ ...form, purchase_uom: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rm-purchase-uom" className={inp}><SelectValue placeholder="Same as UOM" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— Same as UOM —</SelectItem>
                    {(uomOpts.length ? uomOpts : UOMS.map(u => ({ id: u, code: u, symbol: u, name: u })))
                      .map(o => <SelectItem key={o.id || o.code} value={o.symbol || o.code}>{o.symbol || o.code}{o.name && o.name !== o.code ? ` · ${o.name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Conversion Factor" hint="Purchase UOM → Stock UOM">
                <input data-testid="rm-conv-factor" type="number" step="0.001" min="0" value={form.conversion_factor} onChange={e => setForm({ ...form, conversion_factor: e.target.value })} className={inpNum} />
              </F>
              <F label="Reorder Policy">
                <Select value={form.reorder_policy_id || "__none__"} onValueChange={v => setForm({ ...form, reorder_policy_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rm-reorder-policy" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {reorderPolicies.map(p => <SelectItem key={p.id} value={p.id}>{p.name} <span className="text-zinc-500 text-xs">({p.method})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Safety Stock">
                <input data-testid="rm-safety-stock" type="number" step="0.01" min="0" value={form.safety_stock} onChange={e => setForm({ ...form, safety_stock: e.target.value })} className={inpNum} />
              </F>
              <F label="Max Stock">
                <input data-testid="rm-max-stock" type="number" step="0.01" min="0" value={form.max_stock} onChange={e => setForm({ ...form, max_stock: e.target.value })} className={inpNum} />
              </F>
              <F label="Reorder Qty">
                <input data-testid="rm-reorder-qty" type="number" step="0.01" min="0" value={form.reorder_qty} onChange={e => setForm({ ...form, reorder_qty: e.target.value })} className={inpNum} />
              </F>
              <F label="Default Warehouse" hint="Pre-selects on GRN">
                <Select value={form.default_warehouse_id || "__none__"} onValueChange={v => setForm({ ...form, default_warehouse_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rm-default-warehouse" className={inp}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {godowns.map(g => <SelectItem key={g.id} value={g.id}>{g.name} <span className="text-zinc-500 text-xs">({g.kind})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Tracking">
                <div className="flex items-center gap-3 h-9 text-xs">
                  <label className="flex items-center gap-1.5">
                    <input data-testid="rm-batch-tracking" type="checkbox" checked={!!form.batch_tracking} onChange={e => setForm({ ...form, batch_tracking: e.target.checked })} />
                    <span className="text-zinc-400">Batch</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input data-testid="rm-lot-tracking" type="checkbox" checked={form.lot_tracking !== false} onChange={e => setForm({ ...form, lot_tracking: e.target.checked })} />
                    <span className="text-zinc-400">Lot</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input data-testid="rm-expiry" type="checkbox" checked={!!form.expiry_applicable} onChange={e => setForm({ ...form, expiry_applicable: e.target.checked })} />
                    <span className="text-zinc-400">Expiry</span>
                  </label>
                </div>
              </F>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="rm-save" disabled={saving} onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:opacity-90 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Saving…" : (editing ? "Update" : "Create")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · RM Delete Warning · surfaces every BOM (and other
          transaction) that references this RM so the operator can update
          affected BOMs before removing the master row. Delete stays
          blocked while any live reference exists — this is a preview,
          not a bypass. */}
      <Dialog open={!!deleteWarning} onOpenChange={(o) => { if (!o) setDeleteWarning(null); }}>
        <DialogContent className="max-w-2xl bg-[var(--bg-1)] border-[var(--border)]" data-testid="rm-delete-warning-dialog" description="This raw material is still referenced by active transactions — review the linked docs before deactivating.">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <WarningIcon size={18} weight="fill" />
              Cannot delete {deleteWarning?.rm?.code}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-xs">
            <div className="border-l-2 border-red-500/50 bg-red-500/5 px-3 py-2 rounded-sm text-zinc-300">
              This raw material is still referenced by the records below.
              Deleting now would make them show <span className="font-mono text-red-300">[DELETED]</span> chips in their editors.
              Update those records first, then retry delete.
            </div>

            {/* BOM detail — the whole point of this dialog */}
            {(deleteWarning?.usage?.details?.boms || []).length > 0 && (
              <div>
                <div className="overline mb-2 flex items-center justify-between">
                  <span>Bill of Materials · {deleteWarning.usage.details.boms.length}</span>
                  <span className="text-zinc-500 normal-case tracking-normal">Open BOM Master to re-pick a live RM</span>
                </div>
                <div className="border border-[var(--border)] rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[var(--bg-2)]/60 text-[9px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Product Code</th>
                        <th className="px-3 py-1.5 text-left w-20">Version</th>
                        <th className="px-3 py-1.5 text-left w-24">Status</th>
                        <th className="px-3 py-1.5 text-right w-28">Lines Affected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deleteWarning.usage.details.boms.map((b, i) => (
                        <tr key={b.bom_id} data-testid={`rm-delete-warn-bom-${i}`} className="border-t border-[var(--border)]">
                          <td className="px-3 py-1.5 font-mono text-[var(--accent)]">{b.product_code || "—"}</td>
                          <td className="px-3 py-1.5 font-mono">v{b.version}</td>
                          <td className="px-3 py-1.5">
                            {b.is_active
                              ? <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">Active</span>
                              : <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-zinc-500/40 text-zinc-400 bg-zinc-500/10">Inactive</span>}
                          </td>
                          <td className="px-3 py-1.5 font-mono tabular-nums text-right text-amber-300">{b.affected_lines}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Other referenced surfaces (POs, PRs, lots) — counts only */}
            {deleteWarning?.usage?.reasons?.length > 0 && (
              <div>
                <div className="overline mb-2">Other Live References</div>
                <ul className="space-y-1">
                  {deleteWarning.usage.reasons
                    .filter(r => !/BOM/i.test(r))
                    .map((r, i) => (
                      <li key={i} data-testid={`rm-delete-warn-reason-${i}`}
                          className="font-mono text-zinc-300 border border-[var(--border)] rounded-sm px-3 py-1.5 bg-[var(--bg-2)]/40">
                        {r}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              data-testid="rm-delete-warn-close"
              onClick={() => setDeleteWarning(null)}
              className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-300 hover:bg-[var(--bg-2)]">
              Understood — I'll update those first
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 07, 2026 · RM Code Patch — Apply Shade / Promote to Commercial */}
      <Dialog open={!!shadeTarget} onOpenChange={(v) => !v && setShadeTarget(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-xl" data-testid="rm-shade-dialog" description="Promote a Development RM to its Commercial ID by stamping a shade number.">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-widest text-sm">Apply Shade · Development → Commercial</DialogTitle>
          </DialogHeader>
          {shadeTarget && (
            <div className="space-y-4 py-2">
              <div className="rounded-sm border border-[var(--border)] p-3 bg-[var(--bg-2)]/40">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Development ID</div>
                <div className="font-mono text-[var(--accent)]" data-testid="rm-shade-old-code">{shadeTarget.code}</div>
                <div className="text-xs text-zinc-400 mt-1">{shadeTarget.name}</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Shade Number</label>
                <input
                  data-testid="rm-shade-input"
                  value={shadeInput}
                  onChange={(e) => setShadeInput(e.target.value)}
                  onBlur={previewShade}
                  placeholder="e.g. 10234 · Shade A · DYE-01"
                  className="mt-1 w-full h-9 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm px-3 text-sm font-mono focus:outline-none focus:border-[var(--accent)]"
                />
                <div className="text-[10px] text-zinc-500 mt-1">1-32 chars · letters, digits, `-`, `_`. Uppercased on save.</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Reason (for the Audit Log)</label>
                <textarea
                  data-testid="rm-shade-reason"
                  value={shadeReason}
                  onChange={(e) => setShadeReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Dye trial approved for bulk production — batch 26-27-014"
                  className="mt-1 w-full bg-[var(--bg-2)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]"
                />
              </div>
              {shadePreview?.blocked_reason && (
                <div data-testid="rm-shade-blocked" className="rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 text-xs p-3 flex items-start gap-2">
                  <WarningIcon size={14} weight="bold" className="mt-0.5" />
                  <span>{shadePreview.blocked_reason}</span>
                </div>
              )}
              {shadePreview?.proposed_code && !shadePreview.blocked_reason && (
                <div className="rounded-sm border border-purple-500/40 bg-purple-500/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-purple-300 mb-1">Commercial ID (preview)</div>
                  <div className="font-mono text-purple-200" data-testid="rm-shade-preview-code">{shadePreview.proposed_code}</div>
                </div>
              )}
              {shadePreview?.impact_counts && Object.keys(shadePreview.impact_counts).length > 0 && (
                <div className="rounded-sm border border-[var(--border)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Cascading Update — rows that will be renamed</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {Object.entries(shadePreview.impact_counts).map(([coll, n]) => (
                      <div key={coll} className="flex justify-between font-mono">
                        <span className="text-zinc-400">{coll}</span>
                        <span className="text-emerald-300">{n}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              data-testid="rm-shade-cancel"
              onClick={() => setShadeTarget(null)}
              className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-300 hover:bg-[var(--bg-2)]">
              Cancel
            </button>
            <button
              data-testid="rm-shade-submit"
              disabled={shadeBusy || !shadeInput.trim() || !shadeReason.trim() || !!shadePreview?.blocked_reason}
              onClick={applyShade}
              className="px-4 py-2 rounded-sm bg-purple-500 hover:bg-purple-400 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              {shadeBusy ? "Promoting…" : "Promote to Commercial"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
